import {
  type ChangeEvent,
  type FC,
  type ReactNode,
  useContext,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { FlowBuilderContext } from '../context';
import Icon, { IconSizing } from '__components/Kizen/Icon';
import { Button as ButtonKDS } from '@kizen/kds/Button';
import LoadingButton from '__components/Button/LoadingButton';
import BackButton from '__components/Button/BackButton';
import Draggable from '__pages/AutomationEngine/Draggable';
import useModal from '__components/Modals/useModal';
import {
  SMART_CONNECTOR_EXECUTION_STATUSES,
  SMART_CONNECTOR_STATUSES,
  SMARTCONNECTORS_PATHNAME,
} from 'pages/SmartConnectors/constants';
import type { SmartConnectorStatus } from 'pages/SmartConnectors/types';
import { NameInput } from './NameInput';
import {
  Wrapper,
  Left,
  Center,
  Right,
  StepSelector,
  Switch,
  StyledConfirmationModal,
} from './styles';
import { RunSmartConnectorModal } from 'pages/SmartConnectors/components/RunSmartConnectorModal';
import { useRunSmartConnectorModal } from 'pages/SmartConnectors/components/RunSmartConnectorModal/useRunSmartConnectorModal';
import SmartConnectorService from 'services/SmartConnectorService';
import Button from '__components/Button';
import { useHistory } from 'react-router-dom';
import { monitoringExceptionHelper } from 'instrumentation/helpers';
import steps from '../steps';
import styled from '@emotion/styled';
import { shadowOverlay } from 'app/colors';
import { KizenTypography } from '__app/typography';
import { borderRadii } from 'app/spacing';
import { css } from '@emotion/core';
import { useTooltip, truncationPredicates } from '__components/Kizen/Tooltip';

const overflowXOrY = (el: HTMLElement) =>
  truncationPredicates.x(el) || truncationPredicates.y(el);

const OptionWrapper = styled.div<{
  backgroundColor?: string;
  color?: string;
  disabled?: boolean;
}>`
  height: 36px;
  display: flex;
  border-radius: ${borderRadii.small};
  overflow: hidden;
  ${shadowOverlay}
  > div:first-of-type {
    width: 3px;
    background-color: ${({ color }) => color};
  }
  > div:last-child {
    display: flex;
    align-items: center;
    flex: 1;
    padding: 0 8px;
    border: 1px solid var(--color-border-medium);
    border-left: none;
    border-top-right-radius: ${borderRadii.small};
    border-bottom-right-radius: ${borderRadii.small};
    background-color: ${({ backgroundColor }) =>
      backgroundColor || 'var(--color-background-white)'};
    .ReorderIcon {
      color: var(--color-button-tertiary-default);
    }
    ${({ disabled }) =>
      !disabled &&
      css`
        cursor: grab;
        &:hover .ReorderIcon {
          color: var(--color-button-tertiary-hover);
        }
      `}
  }
  & > .Handle * {
    pointer-events: none;
  }
`;

const HandleIconStyles = styled(IconSizing)`
  margin-right: 15px;
`;

const IconStyles = styled(IconSizing)`
  margin-right: 10px;
  color: var(--color-font-primary);
`;

const Label = styled(KizenTypography)`
  margin-top: 2px; // Design nudge
  text-overflow: ellipsis; // ensure truncation when translated
  overflow: hidden;
  white-space: nowrap;
  max-width: 65px;
`;

const StepOption: FC<{
  label: string;
  icon: ReactNode;
  color: string;
  backgroundColor: string;
  disabled?: boolean;
}> = ({ label, icon, ...props }) => {
  const [tooltipProps, tooltip] = useTooltip({}, overflowXOrY);
  return (
    <OptionWrapper {...props}>
      <div />
      <div className="Handle">
        <HandleIconStyles size="15px">
          <Icon className="ReorderIcon" icon="reorder" />
        </HandleIconStyles>
        <IconStyles size="15px">{icon}</IconStyles>
        <Label as="span" {...tooltipProps}>
          {label}
        </Label>
        {tooltip}
      </div>
    </OptionWrapper>
  );
};

StepOption.displayName = 'StepOption';

type ControlBarProps = {
  className: string;
};

export function ControlBar({ className }: ControlBarProps) {
  const { t } = useTranslation();

  const history = useHistory();

  const {
    preRelease,
    smartConnector,
    status,
    setStatus,
    saveable,
    persisting,
    placeStep,
    saveFlow,
  } = useContext(FlowBuilderContext);

  const [queuedExecutionsCount, setQueuedExecutionsCount] = useState(0);
  const [checkingExecutions, setCheckingExecutions] = useState(false);

  const [queuedExecutionsModalProps, , { show: showQueuedExecutionsModal }] =
    useModal({
      handleSubmit: () => {
        setStatus(SMART_CONNECTOR_STATUSES.operational);
      },
      processHideOnSubmit: true,
    });

  const { runSmartConnectorModalProps, showRunSmartConnectorModal } =
    useRunSmartConnectorModal();

  const handleStartDryRun = () => {
    showRunSmartConnectorModal({
      smartConnector,
      isDryRun: true,
      canSkipDiffCheck: smartConnector.is_diff_check_enabled,
    });
  };

  const handleToggleStatus = (ev: ChangeEvent<HTMLInputElement>) => {
    setStatus(
      ev.target.checked
        ? SMART_CONNECTOR_STATUSES.operational
        : SMART_CONNECTOR_STATUSES.inactive
    );
    if (
      !(
        [
          SMART_CONNECTOR_STATUSES.operational,
          SMART_CONNECTOR_STATUSES.need_attention,
        ] as SmartConnectorStatus[]
      ).includes(smartConnector.status) &&
      ev.target.checked
    ) {
      setCheckingExecutions(true);
      SmartConnectorService.getSmartConnectorExecutions(smartConnector.id, {
        params: {
          status: SMART_CONNECTOR_EXECUTION_STATUSES.queued,
          page: 1,
          page_size: 1,
          include_dry_run: false,
        },
      })
        .then(({ data }) => {
          if (data.count > 0) {
            setStatus(smartConnector.status);
            showQueuedExecutionsModal();
            setQueuedExecutionsCount(data.count);
          }
        })
        .catch((err) =>
          monitoringExceptionHelper(err, {
            extra: {
              smartConnector,
            },
          })
        )
        .finally(() => setCheckingExecutions(false));
    }
  };

  const handleGotoSettings = () =>
    history.push(`/smartconnectors/${smartConnector.id}/settings`);

  const handleGotoExecutions = () =>
    history.push(`/smartconnectors/${smartConnector.id}/settings/executions`);

  const handleGotoTimeline = () =>
    history.push(`/smartconnectors/${smartConnector.id}/settings/timeline`);

  return (
    <Wrapper className={className}>
      <div>
        <StepSelector columns={preRelease ? 3 : 1}>
          <div>
            {preRelease ? (
              <>
                <Draggable
                  onStart={() => {
                    placeStep({ type: 'action' });
                  }}
                  onStop={() => placeStep(false)}
                  disabled={false}
                >
                  <StepOption
                    color="#32C055" // "var(--color-agentic-workflow-action-primary)"
                    backgroundColor="#F8FFFA" // "var(--color-agentic-workflow-action-secondary)"
                    icon={<Icon icon="bullhorn" />}
                    label={t('Action')}
                    disabled={false}
                  />
                </Draggable>
                <Draggable
                  onStart={() => placeStep({ type: 'condition' })}
                  onStop={() => placeStep(false)}
                  disabled={false}
                >
                  <StepOption
                    color="#4926cc" // "var(--color-agentic-workflow-condition-primary)"
                    backgroundColor="#F8FFFA" // "var(--color-agentic-workflow-action-secondary)"
                    icon={<Icon icon="code-branch" />}
                    label={t('Condition')}
                    disabled={false}
                  />
                </Draggable>
              </>
            ) : null}
            <Draggable
              onStart={() => placeStep({ type: steps.upload.type })}
              onStop={() => placeStep(false)}
              disabled={!steps.upload.creatable}
            >
              <StepOption
                color="#f3a800" // "var(--color-agentic-workflow-delay-primary)"
                backgroundColor="#fffcf8" // "var(--color-agentic-workflow-delay-secondary)"
                icon={<Icon icon="cloud-upload" />}
                label={t('Upload')}
                disabled={!steps.upload.creatable}
              />
            </Draggable>
          </div>
        </StepSelector>
        <Left>
          <BackButton to={`${SMARTCONNECTORS_PATHNAME}`}>
            {t('Back')}
          </BackButton>
          {/* 
          temporary hidden https://kizen.atlassian.net/browse/KZN-11990
          showStats and setShowStats are exposed by FlowBuilderContext
          <Switch
            textPlacement="right"
            label={t('Stats')}
            checked={showStats}
            onChange={(ev: ChangeEvent<HTMLInputElement>) =>
              setShowStats(ev.target.checked)
            }
            data-qa="smartconnector-stats-toggle"
          /> */}
          <ButtonKDS
            onClick={handleGotoSettings}
            leftIcon="nav-tab-settings"
            leftIconSettings={{
              size: '2xl',
              tooltipDescriptionOverride: t('Settings'),
            }}
            variant="text"
            color="secondary"
          />
          <ButtonKDS
            onClick={handleGotoExecutions}
            leftIcon="custom-play"
            leftIconSettings={{
              size: '2xl',
              tooltipDescriptionOverride: t('Executions'),
            }}
            variant="text"
            color="secondary"
          />
          <ButtonKDS
            onClick={handleGotoTimeline}
            leftIcon="custom-timeline-arrow"
            leftIconSettings={{
              size: '2xl',
              tooltipDescriptionOverride: t('Timeline'),
            }}
            variant="text"
            color="secondary"
          />
        </Left>
        <Center>
          <NameInput />
        </Center>
        <Right>
          <Switch
            textPlacement="left"
            label={t('Active?')}
            checked={(
              [
                SMART_CONNECTOR_STATUSES.operational,
                SMART_CONNECTOR_STATUSES.need_attention,
              ] as SmartConnectorStatus[]
            ).includes(status)}
            onChange={handleToggleStatus}
            backgroundColor="var(--color-button-warning-hover)"
          />
          <ButtonKDS
            onClick={handleStartDryRun}
            leftIcon="action-smartconnector-dry-run"
            leftIconSettings={{
              size: '2xl',
            }}
            variant="text"
            color="secondary"
          />
          <LoadingButton
            disabled={!saveable}
            onClick={saveFlow}
            loading={persisting || checkingExecutions}
          >
            {t('Save')}
          </LoadingButton>
        </Right>
      </div>
      {runSmartConnectorModalProps.show ? (
        <RunSmartConnectorModal {...runSmartConnectorModalProps} />
      ) : null}
      {queuedExecutionsModalProps.show ? (
        <StyledConfirmationModal
          heading={t('{{ executionsCount }} Executions are currently pending', {
            executionsCount: queuedExecutionsCount,
          })}
          rightBtn={
            <Button color="blue">{t('View Pending Executions')}</Button>
          }
          rightBtnClickHandler={handleGotoExecutions}
          leftBtn={<Button variant="text" color="blue" />}
          buttonText={t('Continue')}
          width="460px"
          {...queuedExecutionsModalProps}
        >
          {t(
            'After activation, when the SmartConnector is saved, it will resume processing {{ executionsCount }} executions at a time.',
            {
              executionsCount: queuedExecutionsCount,
            }
          )}
        </StyledConfirmationModal>
      ) : null}
    </Wrapper>
  );
}
